import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, MailCheck, TriangleAlert } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { EuStars } from "../components/EuStars";

export default function VerifyEmail() {
    const [params] = useSearchParams();
    const token = params.get("token");
    const location = useLocation();
    const navigate = useNavigate();
    const { user, refresh, setSession } = useAuth();
    const [state, setState] = useState(token ? "verifying" : "pending");
    const [error, setError] = useState("");
    const [devUrl, setDevUrl] = useState(location.state?.devUrl || "");
    const [resending, setResending] = useState(false);
    const ranRef = useRef(false);

    useEffect(() => {
        if (!token || ranRef.current) return;
        ranRef.current = true;
        api.post("/auth/verify-email", { token })
            .then(({ data }) => {
                if (setSession) setSession({ token: data.token, user: data.user });
                setState("success");
            })
            .catch((err) => {
                const d = err?.response?.data?.detail;
                setError(typeof d === "string" ? d : "This verification link is invalid or has expired.");
                setState("error");
            });
    }, [token, setSession]);

    async function resend() {
        setResending(true);
        try {
            const { data } = await api.post("/auth/resend-verification", {});
            toast.success(data.message || "Verification email sent.");
            if (data.dev_verify_url) setDevUrl(data.dev_verify_url);
        } catch {
            toast.error("Could not resend. Are you signed in?");
        } finally {
            setResending(false);
        }
    }

    async function checkVerified() {
        const u = await refresh();
        if (u?.email_verified) {
            navigate("/app/onboarding", { replace: true });
        } else {
            toast("Not verified yet. Click the link in your email first.");
        }
    }

    const email = location.state?.email || user?.email || "your inbox";

    return (
        <div className="min-h-screen bg-[#F5F0E8] text-[#0D1B3E] flex flex-col">
            <header className="container-edit py-6">
                <Link to="/" className="flex items-center gap-3">
                    <EuStars size={32} />
                    <span className="font-display text-xl tracking-tight">EuroLaw Hub</span>
                </Link>
            </header>
            <main className="flex-1 flex items-center justify-center p-6">
                <div className="w-full max-w-md card-flat p-8 bg-white/80" data-testid="verify-email-card">
                    {state === "verifying" && (
                        <p className="text-[#0D1B3E]/60 flex items-center gap-2" data-testid="verify-email-loading">
                            <Loader2 size={16} className="animate-spin" /> Verifying your email…
                        </p>
                    )}

                    {state === "success" && (
                        <>
                            <CheckCircle2 size={30} color="#2f7d43" />
                            <h1 className="font-display text-2xl lg:text-3xl mt-4 tracking-tight" data-testid="verify-email-success">
                                Email verified.
                            </h1>
                            <p className="mt-3 text-sm text-[#0D1B3E]/75">
                                Your account is active. Start your 7-day free trial (no credit
                                card required) or choose a plan.
                            </p>
                            <button
                                type="button"
                                onClick={() => navigate("/app/onboarding", { replace: true })}
                                className="btn-primary w-full mt-7"
                                data-testid="verify-email-continue"
                            >
                                Choose your trial
                            </button>
                        </>
                    )}

                    {state === "error" && (
                        <>
                            <TriangleAlert size={28} color="#8B1E1E" />
                            <h1 className="font-display text-2xl mt-4 tracking-tight" data-testid="verify-email-error">
                                {error}
                            </h1>
                            <p className="mt-3 text-sm text-[#0D1B3E]/70">
                                Request a fresh link below. Links expire after 48 hours.
                            </p>
                            <button
                                type="button"
                                onClick={resend}
                                disabled={resending}
                                className="btn-secondary w-full mt-6"
                                data-testid="verify-email-resend"
                            >
                                {resending ? "Sending…" : "Resend verification email"}
                            </button>
                            {devUrl && <DevLink url={devUrl} />}
                        </>
                    )}

                    {state === "pending" && (
                        <>
                            <MailCheck size={30} color="#C9A84C" />
                            <div className="label-eyebrow mt-4">One more step</div>
                            <h1 className="font-display text-2xl lg:text-3xl mt-2 tracking-tight" data-testid="verify-email-pending">
                                Check your inbox.
                            </h1>
                            <p className="mt-3 text-sm text-[#0D1B3E]/75">
                                We sent a one-click verification link to <strong>{email}</strong>.
                                Your 7-day free trial activates as soon as you verify.
                            </p>
                            {devUrl && <DevLink url={devUrl} />}
                            <div className="mt-7 space-y-3">
                                <button
                                    type="button"
                                    onClick={checkVerified}
                                    className="btn-primary w-full"
                                    data-testid="verify-email-check"
                                >
                                    I've clicked the link, continue
                                </button>
                                <button
                                    type="button"
                                    onClick={resend}
                                    disabled={resending}
                                    className="btn-secondary w-full"
                                    data-testid="verify-email-resend"
                                >
                                    {resending ? "Sending…" : "Resend the email"}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}

function DevLink({ url }) {
    return (
        <div className="mt-5 border border-dashed border-[#C9A84C]/60 bg-[#FFFDF7] p-3 text-xs" data-testid="verify-email-devlink">
            <span className="uppercase tracking-[0.18em] text-[#0D1B3E]/50 block mb-1.5">
                Didn't receive the email?
            </span>
            <Link to={url} className="underline text-[#0D1B3E] break-all" data-testid="verify-email-devlink-open">
                Open your verification link
            </Link>
        </div>
    );
}
